import { TokenParser } from "@livefree/react-oauth2";
import { createContext, useContext, useSyncExternalStore, createElement, Fragment } from "react";
import { jsx } from "react/jsx-runtime";
import { BaseSDK, BaseEndpoint } from "@livefree/js-sdk";
function normalizeUserId(id) {
  if (id === void 0 || id === null) return 0;
  const n = typeof id === "number" ? id : Number.parseInt(String(id), 10);
  return Number.isNaN(n) ? 0 : n;
}
class User {
  #id;
  /** Raw id used for storage key so read matches write (same format as loadRolesAndPoliciesFromAuthority). */
  #storageUserId;
  #firstName;
  #lastName;
  #displayName;
  #email;
  #phone;
  #picture;
  #roles;
  #policies;
  #applicationKey;
  #repository;
  #rolesAndPoliciesLoaded = false;
  constructor(user, options) {
    this.#id = normalizeUserId(user.id);
    this.#storageUserId = user.id ?? 0;
    this.#firstName = user.firstName ?? "";
    this.#lastName = user.lastName ?? "";
    this.#displayName = user.displayName ?? "";
    this.#email = user.email ?? "";
    this.#phone = user.phone ?? "";
    this.#picture = user.picture ?? "";
    this.#roles = [];
    this.#policies = [];
    this.#applicationKey = options?.applicationKey;
    this.#repository = options?.rolesAndPoliciesRepository ?? null;
  }
  #ensureRolesAndPoliciesFromStorage() {
    if (this.#rolesAndPoliciesLoaded || this.#applicationKey == null || this.#repository == null)
      return;
    const { roles, policies } = this.#repository.getRolesAndPolicies(
      this.#storageUserId,
      this.#applicationKey
    );
    this.#roles = roles;
    this.#policies = policies;
    this.#rolesAndPoliciesLoaded = true;
  }
  get id() {
    return this.#id;
  }
  get firstName() {
    return this.#firstName;
  }
  get lastName() {
    return this.#lastName;
  }
  get displayName() {
    return this.#displayName;
  }
  get email() {
    return this.#email;
  }
  get phone() {
    return this.#phone;
  }
  get picture() {
    return this.#picture;
  }
  get policies() {
    this.#ensureRolesAndPoliciesFromStorage();
    return this.#policies;
  }
  get roles() {
    this.#ensureRolesAndPoliciesFromStorage();
    return this.#roles;
  }
  /**
   * Fails loudly on a missing apiKey. Without this the call either crashes later with an opaque
   * `toLowerCase` TypeError (only when the user has policies/roles) or silently returns false.
   */
  #requireApiKey(method, apiKey) {
    if (!apiKey) {
      throw new Error(`User.${method}: apiKey is required (read it from config.api.apiKey)`);
    }
  }
  #matchesPolicy(policy, apiKey, resource, operation, context) {
    return String(policy.userId) === String(this.#id) && policy.apiKey.toLowerCase() === apiKey.toLowerCase() && policy.resource.toLowerCase() === resource.toLowerCase() && policy.operation.toLowerCase() === operation.toLowerCase() && (context ? (policy.context ?? "").toLowerCase() === context.toLowerCase() : true);
  }
  /**
   * Check if user has the specified policy.
   * Only one of resource or operation may be an array; the other must be a string.
   * @param rule - When resource or operation is an array: "any" = true if at least one matches; "all" = true only if all match.
   */
  hasPolicy(apiKey, resource, operation, context = null, rule = "any") {
    this.#requireApiKey("hasPolicy", apiKey);
    this.#ensureRolesAndPoliciesFromStorage();
    if (!Array.isArray(this.#policies)) return false;
    const resourceIsArray = Array.isArray(resource);
    const operationIsArray = Array.isArray(operation);
    if (resourceIsArray && operationIsArray) return false;
    if (typeof resource === "string" && typeof operation === "string") {
      const policy = this.#policies.find(
        (x) => this.#matchesPolicy(x, apiKey, resource, operation, context)
      );
      return !!policy;
    }
    if (operationIsArray) {
      const ops = Array.from(operation);
      if (ops.length === 0) return false;
      const res = resource;
      if (rule === "any") {
        return ops.some(
          (op2) => this.#policies.some((x) => this.#matchesPolicy(x, apiKey, res, op2, context))
        );
      }
      return ops.every(
        (op2) => this.#policies.some((x) => this.#matchesPolicy(x, apiKey, res, op2, context))
      );
    }
    const resources = Array.from(resource);
    if (resources.length === 0) return false;
    const op = operation;
    if (rule === "any") {
      return resources.some(
        (res) => this.#policies.some((x) => this.#matchesPolicy(x, apiKey, res, op, context))
      );
    }
    return resources.every(
      (res) => this.#policies.some((x) => this.#matchesPolicy(x, apiKey, res, op, context))
    );
  }
  /**
   * Check if the user has the specified role.
   */
  hasRole(apiKey, role) {
    this.#requireApiKey("hasRole", apiKey);
    this.#ensureRolesAndPoliciesFromStorage();
    if (!Array.isArray(this.#roles)) return false;
    const r = this.#roles.find(
      (x) => String(x.userId) === String(this.#id) && x.apiKey.toLowerCase() === apiKey.toLowerCase() && x.role.toLowerCase() === role.toLowerCase()
    );
    return !!r;
  }
  hasApiAccess(apiKey) {
    this.#requireApiKey("hasApiAccess", apiKey);
    this.#ensureRolesAndPoliciesFromStorage();
    if (!Array.isArray(this.#policies)) return false;
    return this.#policies.some(
      (x) => x.apiKey.toLowerCase() === apiKey.toLowerCase()
    );
  }
}
const STORAGE_KEY_PREFIX = "livefree.rolesAndPolicies";
function storageKey(userId, applicationKey) {
  return `${STORAGE_KEY_PREFIX}.${String(userId)}.${applicationKey}`;
}
async function loadRolesAndPoliciesToStorage(client, userId, applicationKey) {
  const payload = await client.getUserRolesAndPolicies(userId, applicationKey);
  setRolesAndPoliciesInStorage(userId, applicationKey, payload);
  return payload;
}
function getRolesAndPoliciesFromStorage(userId, applicationKey) {
  const key = storageKey(userId, applicationKey);
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return { roles: [], policies: [] };
    const parsed = JSON.parse(raw);
    return {
      roles: Array.isArray(parsed.roles) ? parsed.roles : [],
      policies: Array.isArray(parsed.policies) ? parsed.policies : []
    };
  } catch {
    return { roles: [], policies: [] };
  }
}
function setRolesAndPoliciesInStorage(userId, applicationKey, payload) {
  const key = storageKey(userId, applicationKey);
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (e) {
    console.warn("[@livefree/runtime] Could not write roles/policies to localStorage", e);
  }
}
class LocalStorageRolesAndPoliciesRepository {
  getRolesAndPolicies(userId, applicationKey) {
    return getRolesAndPoliciesFromStorage(userId, applicationKey);
  }
}
const FALLBACK_USER_DATA = {
  id: 0,
  firstName: "",
  lastName: "",
  displayName: "",
  email: "",
  phone: "",
  picture: ""
};
function createDefaultGetUser(applicationKey) {
  return function getDefaultUser() {
    const oauth2User = TokenParser.user;
    const userData = oauth2User ? {
      id: oauth2User.id,
      firstName: oauth2User.firstName,
      lastName: oauth2User.lastName,
      displayName: oauth2User.displayName,
      email: oauth2User.email,
      phone: oauth2User.phone,
      picture: oauth2User.picture
    } : FALLBACK_USER_DATA;
    return new User(userData, {
      applicationKey,
      rolesAndPoliciesRepository: new LocalStorageRolesAndPoliciesRepository()
    });
  };
}
class CoreEvent {
  name;
  listener;
  /**
   * @param name - Custom event name to dispatch/listen on.
   */
  constructor(name) {
    this.name = name;
  }
  /**
   * Dispatch a payload to registered event listeners.
   */
  dispatch(detail) {
    if (typeof document === "undefined" || !document.body) return;
    const payload = { detail: { ...detail } };
    document.body.dispatchEvent(new CustomEvent(this.name, payload));
  }
  /**
   * Register a function to listen for when the event is dispatched.
   */
  listen(listener) {
    if (typeof document === "undefined" || !document.body) return;
    this.listener = listener;
    document.body.addEventListener(this.name, listener, false);
  }
  /**
   * Unregister listener function.
   */
  unsubscribe() {
    if (typeof document === "undefined" || !document.body) return;
    if (!this.listener) return;
    document.body.removeEventListener(this.name, this.listener);
  }
}
const defaultLocation = typeof window !== "undefined" ? window.location : void 0;
const defaultAlertParams = {
  title: "",
  summary: "",
  details: null,
  actionTitle: null,
  actionUrl: null,
  location: defaultLocation,
  onSuccess: () => "Operation complete!",
  onError: () => "Unexpected error",
  promise: null
};
class AlertEvent extends CoreEvent {
  static type = {
    pending: "pending",
    info: "info",
    success: "success",
    warning: "warning",
    error: "error"
  };
  constructor() {
    super("alert-event");
  }
  dispatch(alert) {
    const merged = {
      ...defaultAlertParams,
      ...alert
    };
    super.dispatch(merged);
  }
}
const emit$2 = (alert) => {
  new AlertEvent().dispatch(alert);
};
const merge$1 = (type, message) => {
  let alert = { type };
  if (typeof message === "string") {
    alert.title = message;
  } else {
    alert = { ...alert, ...message };
  }
  return alert;
};
const info$1 = (message) => {
  emit$2(merge$1(AlertEvent.type.info, message));
};
const success = (message) => {
  emit$2(merge$1(AlertEvent.type.success, message));
};
const warn$1 = (message) => {
  emit$2(merge$1(AlertEvent.type.warning, message));
};
const error$1 = (message) => {
  emit$2(merge$1(AlertEvent.type.error, message));
};
const pending = (promise, message, onSuccess, onError) => {
  const alert = merge$1(AlertEvent.type.pending, message);
  alert.promise = promise;
  if (onSuccess) {
    alert.onSuccess = onSuccess;
  }
  if (onError) {
    alert.onError = onError;
  }
  emit$2(alert);
};
const alerts = {
  info: info$1,
  success,
  warn: warn$1,
  error: error$1,
  pending
};
const defaultLocationString = typeof window !== "undefined" ? JSON.stringify(window.location) : "";
const defaultLogParams = {
  level: "info",
  message: "",
  detail: "",
  location: defaultLocationString
};
class LogEvent extends CoreEvent {
  static level = {
    info: "info",
    warn: "warning",
    error: "error"
  };
  constructor() {
    super("log-event");
  }
  dispatch(log) {
    const merged = {
      ...defaultLogParams,
      ...log
    };
    super.dispatch(merged);
  }
}
const emit$1 = (log) => {
  new LogEvent().dispatch(log);
};
const merge = (level, message) => {
  let log = { level };
  if (typeof message === "string") {
    log.message = message;
  } else if (message instanceof Error) {
    log = {
      ...log,
      message: message.message
    };
  } else {
    log = { ...log, ...message };
  }
  return log;
};
const info = (msg) => {
  emit$1(merge(LogEvent.level.info, msg));
};
const warn = (msg) => {
  emit$1(merge(LogEvent.level.warn, msg));
};
const error = (msg) => {
  emit$1(merge(LogEvent.level.error, msg));
};
const logging = {
  info,
  warn,
  error
};
class ConsoleLoggingProvider {
  log(header, log) {
    console.log(
      `[${header}]: message: ${log.message}, detail: ${log.detail}, location: ${log.location}, provider: ConsoleLoggingProvider`
    );
  }
  info(log) {
    this.log("INFO", log);
  }
  warn(log) {
    this.log("WARN", log);
  }
  error(log) {
    this.log("ERROR", log);
  }
}
class RecentViewEvent extends CoreEvent {
  constructor() {
    super("recent-view-event");
  }
}
const currentPathname = () => typeof window === "undefined" ? void 0 : window.location.pathname;
const emit = (detail) => {
  new RecentViewEvent().dispatch(detail);
};
const announce = ({ title, route, pathname }) => {
  const path = pathname ?? currentPathname();
  if (!path || !title) return;
  emit({ pathname: path, title, route });
};
const views = {
  announce
};
const bySlotOrder = (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
function createComponentRegistry() {
  const entries = /* @__PURE__ */ new Map();
  const listeners = /* @__PURE__ */ new Set();
  let slots = /* @__PURE__ */ new Map();
  const rebuildSlots = () => {
    const next = /* @__PURE__ */ new Map();
    for (const entry of entries.values()) {
      if (!entry.slot) continue;
      const list = next.get(entry.slot) ?? [];
      list.push(entry);
      next.set(entry.slot, list);
    }
    for (const list of next.values()) list.sort(bySlotOrder);
    slots = next;
  };
  const EMPTY = [];
  return {
    register(name, component, options = {}) {
      if (entries.has(name)) {
        throw new Error(`Component "${name}" is already registered.`);
      }
      entries.set(name, {
        name,
        component,
        slot: options.slot,
        order: options.order
      });
      rebuildSlots();
      for (const listener of listeners) listener();
    },
    get(name) {
      return entries.get(name)?.component;
    },
    slot(slot) {
      return slots.get(slot) ?? EMPTY;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    }
  };
}
function defineUserContext(key, load) {
  return { key, load };
}
function createUserContextRegistry(getUser, warn2 = () => {
}) {
  const contexts = /* @__PURE__ */ new Map();
  const values = /* @__PURE__ */ new Map();
  const listeners = /* @__PURE__ */ new Set();
  let hasLoaded = false;
  const notify = () => listeners.forEach((listener) => listener());
  const loadOne = async (context, user) => {
    try {
      values.set(context.key, await context.load(user) ?? null);
    } catch (error2) {
      values.set(context.key, null);
      warn2({
        message: `[@livefree/runtime] User context "${context.key}" failed to load.`,
        error: error2 instanceof Error ? error2.message : String(error2)
      });
    }
  };
  return {
    register(context) {
      if (contexts.has(context.key)) {
        throw new Error(`User context "${context.key}" is already registered.`);
      }
      contexts.set(context.key, context);
      if (hasLoaded) void loadOne(context, getUser()).then(notify);
    },
    get(context) {
      const key = typeof context === "string" ? context : context.key;
      return values.get(key) ?? null;
    },
    async load() {
      const user = getUser();
      await Promise.all(Array.from(contexts.values(), (context) => loadOne(context, user)));
      hasLoaded = true;
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }
  };
}
function createRuntimeApi(getConfig, getUser, components, userContexts) {
  const alerts$1 = {
    info(title, message) {
      alerts.info(message ? { title, summary: message } : { title });
    },
    success(title, message) {
      alerts.success(message ? { title, summary: message } : { title });
    },
    warn(title, message) {
      alerts.warn(message ? { title, summary: message } : { title });
    },
    error(title, message) {
      alerts.error(message ? { title, summary: message } : { title });
    }
  };
  const logging$1 = {
    info(message) {
      logging.info(message);
    },
    warn(message) {
      logging.warn(message);
    },
    error(message) {
      logging.error(message);
    }
  };
  const events = {
    dispatch(event, payload) {
      event.dispatch(payload);
    }
  };
  const views$1 = {
    announce(view) {
      views.announce(view);
    }
  };
  const services = {
    alerts: alerts$1,
    logging: logging$1,
    events,
    views: views$1
  };
  return {
    get config() {
      return getConfig();
    },
    get user() {
      return getUser();
    },
    services,
    components,
    userContexts
  };
}
function createRuntime(options) {
  const { hostConfig, getUser: getUserOption } = options;
  const applicationKey = hostConfig.current?.auth?.applicationKey ?? hostConfig.development?.auth?.applicationKey ?? "";
  const getUser = getUserOption ?? createDefaultGetUser(applicationKey);
  const getHostConfig = () => hostConfig.current;
  const registry = /* @__PURE__ */ new Map();
  const components = createComponentRegistry();
  const userContexts = createUserContextRegistry(getUser, (message) => logging.warn(message));
  const api = createRuntimeApi(getHostConfig, getUser, components, userContexts);
  function registerApp(id, appOptions) {
    if (registry.has(id)) {
      throw new Error(`App "${id}" is already registered.`);
    }
    const baseUrl = appOptions.baseUrl ?? `/${id}`;
    const getAppConfig = appOptions.config ?? getHostConfig;
    const appApi = createRuntimeApi(getAppConfig, getUser, components, userContexts);
    const appRuntime = {
      get config() {
        return appApi.config;
      },
      get user() {
        return appApi.user;
      },
      services: appApi.services,
      components,
      userContexts,
      get baseUrl() {
        return baseUrl;
      }
    };
    const descriptor = {
      id,
      baseUrl,
      runtime: appRuntime,
      description: appOptions.description,
      routes: appOptions.routes,
      navigation: () => appOptions.navigation(appRuntime),
      resources: appOptions.resources ? () => appOptions.resources(appRuntime) : void 0
    };
    registry.set(id, descriptor);
    return appRuntime;
  }
  function getApp(id) {
    return registry.get(id);
  }
  function getApps() {
    return Array.from(registry.values());
  }
  return {
    get config() {
      return api.config;
    },
    get user() {
      return api.user;
    },
    services: api.services,
    components,
    userContexts,
    registerApp,
    getApp,
    getApps
  };
}
const RuntimeContext = createContext(null);
function RuntimeProvider({ runtime, children }) {
  return /* @__PURE__ */ jsx(RuntimeContext.Provider, { value: runtime, children });
}
function useRuntimeProvider() {
  const runtime = useContext(RuntimeContext);
  if (runtime === null) {
    throw new Error("useRuntimeProvider must be used within a RuntimeProvider.");
  }
  return runtime;
}
class ApiConfiguration {
  #host = "";
  #swaggerUrl = "";
  #apiKey = "";
  get host() {
    return this.#host;
  }
  set host(value) {
    this.#host = value;
  }
  get apiKey() {
    return this.#apiKey;
  }
  set apiKey(value) {
    this.#apiKey = value;
  }
  get swaggerUrl() {
    return this.#swaggerUrl;
  }
  set swaggerUrl(value) {
    this.#swaggerUrl = value;
  }
  copyFrom(config) {
    this.host = config.host;
    this.apiKey = config.apiKey;
    this.swaggerUrl = config.swaggerUrl;
  }
}
class AuthConfiguration {
  #host = "";
  #clientId = "";
  #applicationKey = "";
  #scopes = "openid";
  get host() {
    return this.#host;
  }
  set host(value) {
    this.#host = value;
  }
  get clientId() {
    return this.#clientId;
  }
  set clientId(value) {
    this.#clientId = value;
  }
  get scopes() {
    return this.#scopes;
  }
  set scopes(value) {
    this.#scopes = value;
  }
  get applicationKey() {
    return this.#applicationKey;
  }
  set applicationKey(value) {
    this.#applicationKey = value;
  }
  copyFrom(config) {
    this.host = config.host;
    this.clientId = config.clientId;
    this.applicationKey = config.applicationKey;
    this.scopes = config.scopes;
  }
}
class DependenciesConfiguration {
  #googleMapsKey = "";
  get googleMapsKey() {
    return this.#googleMapsKey;
  }
  set googleMapsKey(value) {
    this.#googleMapsKey = value;
  }
  copyFrom(config) {
    this.googleMapsKey = config.googleMapsKey;
  }
}
const toHosts = (value) => (typeof value === "string" ? [value] : [...value]).filter((x) => x !== "");
class EnvironmentConfiguration {
  #name;
  #hosts;
  #api;
  #auth;
  #dependencies;
  #extended = /* @__PURE__ */ new Map();
  constructor(name, host = "") {
    this.#name = name;
    this.#hosts = toHosts(host);
    this.#api = new ApiConfiguration();
    this.#auth = new AuthConfiguration();
    this.#dependencies = new DependenciesConfiguration();
  }
  get name() {
    return this.#name;
  }
  /** The primary origin: the first of {@link hosts}, or `''` when there is none. */
  get host() {
    return this.#hosts[0] ?? "";
  }
  /** One origin, or several that all select this environment (e.g. an old and a new domain). */
  set host(value) {
    this.#hosts = toHosts(value);
  }
  /** Every origin that selects this environment, primary first. */
  get hosts() {
    return this.#hosts;
  }
  /** Whether `origin` (e.g. `location.origin`) is one of this environment's {@link hosts}. */
  matches(origin) {
    return origin != null && this.#hosts.includes(origin);
  }
  get api() {
    return this.#api;
  }
  get auth() {
    return this.#auth;
  }
  get dependencies() {
    return this.#dependencies;
  }
  copyFrom(config) {
    this.host = [...config.hosts];
    this.api.copyFrom(config.api);
    this.auth.copyFrom(config.auth);
    this.dependencies.copyFrom(config.dependencies);
    this.extend(Object.fromEntries(config.#extended));
  }
  /**
   * Include additional configuration not already defined.
   *
   * The values are flattened onto this instance, so they are read as `config.myValue` rather than
   * `config.extended.myValue`. Declare their shape with {@link ExtendedConfiguration} to type them.
   */
  extend(config) {
    for (const x of Object.getOwnPropertyNames(config)) {
      this.#extended.set(x, config[x]);
      Object.defineProperty(this, x, {
        configurable: true,
        enumerable: true,
        writable: true,
        value: config[x]
      });
    }
  }
}
class EnvironmentConfigurations {
  #environments = [];
  #extended = {};
  static create() {
    return new EnvironmentConfigurations().add(new EnvironmentConfiguration("development")).add(new EnvironmentConfiguration("integration")).add(new EnvironmentConfiguration("staging")).add(new EnvironmentConfiguration("production"));
  }
  /**
   * Create a new instance and initialize from an object.
   * Top-level properties represent environments.
   * Use `extended` to provide shared extended configuration.
   */
  static hydrate(configurations) {
    const configs = new EnvironmentConfigurations();
    for (const environment of Object.getOwnPropertyNames(configurations)) {
      const values = configurations[environment];
      if (environment === "extended") {
        if (values && typeof values === "object" && !Array.isArray(values))
          configs.extend(values);
        continue;
      }
      const envValues = values;
      if (!envValues) continue;
      const config = new EnvironmentConfiguration(environment, envValues.host ?? "");
      config.api.host = envValues.api?.host ?? "";
      config.api.apiKey = envValues.api?.apiKey ?? "";
      config.api.swaggerUrl = envValues.api?.swaggerUrl ?? "";
      config.auth.host = envValues.auth?.host ?? "";
      config.auth.clientId = envValues.auth?.clientId ?? "";
      config.auth.scopes = envValues.auth?.scopes ?? "";
      config.auth.applicationKey = envValues.auth?.applicationKey ?? "";
      config.dependencies.googleMapsKey = envValues.dependencies?.googleMapsKey ?? "";
      if (Object.hasOwn(envValues, "extended") && envValues.extended)
        config.extend(envValues.extended);
      configs.add(config);
    }
    return configs;
  }
  add(configuration, replace = false) {
    const existing = this.byName(configuration.name);
    if (existing && !replace)
      throw new Error(`${configuration.name} already exists. Did you mean to replace?`);
    if (existing) {
      this.#environments[this.#indexByName(configuration.name)] = configuration;
      return this;
    }
    this.#environments.push(configuration);
    return this;
  }
  get development() {
    return this.byName("development");
  }
  get integration() {
    return this.byName("integration");
  }
  get staging() {
    return this.byName("staging");
  }
  get production() {
    return this.byName("production");
  }
  get all() {
    return this.#environments;
  }
  /**
   * The configuration for the current environment at runtime.
   * Determined by globalThis.location.origin matching one of the environment's hosts.
   */
  get current() {
    const origin = globalThis.location?.origin;
    const environment = this.#environments.find((x) => x.matches(origin));
    if (environment) return this.byName(environment.name);
    return void 0;
  }
  copyFrom(config) {
    this.extend(config.#extended);
    for (const environment of config.all) {
      const existing = this.byName(environment.name);
      if (existing) existing.copyFrom(environment);
      else this.add(environment);
    }
    return this;
  }
  duplicate() {
    const duplicate = new EnvironmentConfigurations();
    return duplicate.copyFrom(this);
  }
  byName(name) {
    const environment = this.#environments.find((x) => x.name === name);
    if (environment) environment.extend(this.#extended);
    return environment;
  }
  extend(config) {
    for (const x of Object.getOwnPropertyNames(config)) {
      Object.defineProperty(this.#extended, x, {
        configurable: true,
        enumerable: true,
        writable: true,
        value: config[x]
      });
    }
    return this;
  }
  #indexByName(name) {
    return this.#environments.findIndex((x) => x.name === name);
  }
}
class UsersEndpoint extends BaseEndpoint {
  constructor(config) {
    super("my", config);
  }
  async getUserRolesAndPolicies(userId, applicationKey) {
    const roles = await this.httpGET({
      url: this.appendUrl("/roles"),
      query: { applicationKey }
    });
    const policies = await this.httpGET({
      url: this.appendUrl("/policies"),
      query: { applicationKey }
    });
    return {
      roles: (roles ?? []).map((x) => ({
        userId,
        apiKey: x.apiRegistrationName,
        role: x.name
      })),
      policies: (policies ?? []).map((x) => ({
        userId,
        apiKey: x.apiRegistrationName,
        resource: x.resourceKey,
        operation: x.operationKey,
        context: x.contextKey
      }))
    };
  }
}
class UserClient extends BaseSDK {
  #users;
  /**
   * @param authorityUrl - Auth host (e.g. config.auth.host). Used as the SDK base URL.
   * @param tokenLoader - Optional. Returns current access token; defaults to TokenParser.accessToken from react-oauth2.
   */
  constructor(authorityUrl, tokenLoader = () => TokenParser.accessToken) {
    const base = authorityUrl.replace(/\/$/, "");
    super(`${base}/api/v1`);
    this.config.onRequireToken = async () => await Promise.resolve(tokenLoader()) ?? null;
    this.#users = new UsersEndpoint(this.config);
  }
  /**
   * Fetches the user's roles and policies from the authority. Token is resolved via the token loader passed to the constructor.
   */
  getUserRolesAndPolicies(userId, applicationKey) {
    return this.#users.getUserRolesAndPolicies(userId, applicationKey);
  }
}
async function loadRolesAndPoliciesFromAuthority(authorityUrl, userId, applicationKey) {
  if (!authorityUrl || !applicationKey) {
    return { roles: [], policies: [] };
  }
  const client = new UserClient(authorityUrl);
  return loadRolesAndPoliciesToStorage(client, userId, applicationKey);
}
function useUserContext(context) {
  const { userContexts } = useRuntimeProvider();
  return useSyncExternalStore(userContexts.subscribe, () => userContexts.get(context));
}
const Empty = () => null;
function useComponent(name) {
  const { components } = useRuntimeProvider();
  return useSyncExternalStore(
    components.subscribe,
    () => components.get(name) ?? Empty
  );
}
function useSlot(slot) {
  const { components } = useRuntimeProvider();
  return useSyncExternalStore(components.subscribe, () => components.slot(slot));
}
function Slot({ name, ...props }) {
  const registrations = useSlot(name);
  return createElement(
    Fragment,
    null,
    ...registrations.map(({ name: key, component }) => createElement(component, { key, ...props }))
  );
}
export {
  AlertEvent,
  ConsoleLoggingProvider,
  CoreEvent,
  EnvironmentConfiguration,
  EnvironmentConfigurations,
  LocalStorageRolesAndPoliciesRepository,
  LogEvent,
  RecentViewEvent,
  RuntimeContext,
  RuntimeProvider,
  Slot,
  User,
  UserClient,
  alerts,
  createComponentRegistry,
  createRuntime,
  createUserContextRegistry,
  defineUserContext,
  getRolesAndPoliciesFromStorage,
  loadRolesAndPoliciesFromAuthority,
  loadRolesAndPoliciesToStorage,
  logging,
  setRolesAndPoliciesInStorage,
  useComponent,
  useRuntimeProvider,
  useSlot,
  useUserContext,
  views
};
//# sourceMappingURL=index.js.map
